import { useChannelWithFallback } from '@/api/hooks'
import DragIcon from '@/app/icons/drag'
import EditIcon from '@/app/icons/edit'
import LockIcon from '@/app/icons/lock'
import OpenIcon from '@/app/icons/open'
import { getChannelGifsById } from '@/app/util/channel-api'
import createPlaceholderGif from '@/app/util/gif-skeleton'
import { cn } from '@/util/cn'
import { IChannel } from '@giphy/js-types'
import { Gif } from '@giphy/react-components'
import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd'
import Link from 'next/link'
import { useContext, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { ClickToShowModal, ModalContext } from '../../modal'
import { Breadcrumbs } from '../ui'

type Props = { channel: IChannel; className?: string; dragHandleProps: DraggableProvidedDragHandleProps | null }
const actionButtonClassName = 'bg-giphyDarkCharcoal flex p-3 rounded-md gap-2 cursor-pointer'
const ChannelRow = ({ channel: _channel, className, dragHandleProps }: Props) => {
    const { setModalType } = useContext(ModalContext)
    const { data: channel = _channel } = useChannelWithFallback(_channel)
    const hasKids = channel.num_children > 0
    const gif = channel.featured_gif || createPlaceholderGif()
    const [count, setCount] = useState<number>()
    const { ref, inView } = useInView()
    useEffect(() => {
        const fetchCount = async () => {
            const data = await getChannelGifsById(channel.id, { offset: 0, limit: 25 }, document.cookie)
            setCount(data.pagination.count)
        }
        if (inView) {
            fetchCount()
        }
    }, [channel, inView])
    return (
        <div
            ref={ref}
            className={cn('flex cursor-pointer bg-giphyDarkGrey hover:bg-giphyCharcoal', className)}
            onClick={() => {
                setModalType({
                    name: 'editCollections',
                    channelId: channel.id,
                    title: <Breadcrumbs channel={channel} />,
                })
            }}
        >
            <Gif
                className={cn(className, 'cursor-pointer [&_img]:object-cover')}
                gif={gif}
                width={120}
                height={80}
                onGifClick={() => {
                    setModalType({
                        name: 'editCollections',
                        channelId: channel.id,
                        title: <Breadcrumbs channel={channel} />,
                    })
                }}
                hideAttribution
                noLink
                overlay={({}) => {
                    return channel.is_private ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <LockIcon className="size-10 fill-giphyRed" />
                        </div>
                    ) : null
                }}
                borderRadius={0}
                backgroundColor={gif.is_sticker ? undefined : 'rgba(0,0,0,0)'}
            />
            <div className="flex grow justify-between p-4">
                <div>
                    <div className="flex gap-2">
                        <span className="line-clamp-1 text-xl font-bold select-none">{channel.display_name}</span>
                        {hasKids && (
                            <div
                                className={cn(
                                    actionButtonClassName,
                                    'rounded-lg border px-2 py-1 text-center font-bold'
                                )}
                                onClick={() => {
                                    setModalType({
                                        name: 'editCollections',
                                        channelId: channel.id,
                                        title: <Breadcrumbs channel={channel} />,
                                    })
                                }}
                            >
                                {channel.num_children} more
                            </div>
                        )}
                    </div>
                    <div className="h-5 text-giphyLightGrey select-none">
                        {count ? (
                            <>
                                <span className="font-bold">{count}</span> items
                            </>
                        ) : (
                            ''
                        )}
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 p-4">
                <Link className={actionButtonClassName} href={channel.url} target="_blank">
                    <OpenIcon className="size-5" />
                    View
                </Link>
                <ClickToShowModal modal={{ name: 'editCollection', channel }} className={actionButtonClassName}>
                    <EditIcon className="size-5" /> Edit
                </ClickToShowModal>
                <div {...dragHandleProps}>
                    <DragIcon className={cn('ml-4 size-5 cursor-grab')} />
                </div>
            </div>
        </div>
    )
}

export default ChannelRow
